/**
 * ENG-9284 follow-up — the ONE list of directories a connectivity probe must be
 * able to see, shared by every lane that builds a probe environment.
 *
 * WHY THIS FILE EXISTS, which is a story about the same bug being fixed twice in
 * two languages and still being live.
 *
 * CLI-backed integrations are verified by running their binary (`gh` for GitHub,
 * and so on). On this fleet those binaries are installed by `[toolkit-install]`
 * THROUGH HOMEBREW, so on `agt-aws-1` the real `gh` exists at exactly one path:
 *
 *   /root/.augmented/<agent>/project/.claude/agt-bin/gh   <- the ENG-8344 shim
 *   /home/linuxbrew/.linuxbrew/bin/gh                     <- the only real binary
 *
 * Nothing in /usr/bin, /usr/local/bin or /opt (measured by marlow, ENG-9284).
 *
 * Three separate places have had to know that, and until this file none of them
 * referenced each other:
 *
 *   1. `manager-worker.ts` prepends Homebrew's bin dir to its OWN
 *      `process.env.PATH` (:4680, :4906, :5477) before running anything.
 *   2. `connectivity-probe-context.ts` appends `SYSTEM_PATH_FLOOR` — the six
 *      dirs `host-bootstrap.ts` writes into /etc/environment. It has NEVER
 *      contained a Homebrew dir.
 *   3. `routes/admin-debug.ts` prepends the Homebrew dirs in SHELL, inside the
 *      SSM command string (ENG-9284, PR #5387).
 *
 * THE CONSEQUENCE, and it is the thing worth reading twice: `buildProbeEnv` is
 * documented as the single definition of "how a probe runs on this host"
 * (ENG-6441), and it cannot find `gh` on its own. It works in the scheduled lane
 * only because the manager mutated its own environment first, and in the forced
 * lane only because a different file patched the calling shell. Both lanes pass
 * today by accident of their caller, not because the function is correct — so a
 * third caller, or a change to either external mechanism, silently reintroduces
 * a fleet-wide false verdict.
 *
 * That is not hypothetical either. The forced lane returned `unverified` for a
 * healthy GitHub integration for eleven days between the two fixes, and marlow
 * recorded on ENG-9271 that it caused a wrong finding about five agents before
 * the instrument, rather than the fleet, was identified as the fault.
 *
 * So: the dirs live here, and the consumers import them. A new toolkit location
 * is added once.
 */

/**
 * Where `[toolkit-install]` puts integration binaries, highest priority first.
 *
 * ORDER IS LOAD-BEARING and it is the manager's order, not a preference. The
 * manager prepends its Homebrew bin dir ahead of everything, so on a host where
 * a binary exists BOTH in Homebrew and in /usr/local/bin — the normal case once
 * brew has installed over a distro copy — the agent runs the Homebrew one. A
 * probe that resolved the other would be measuring a binary the agent never
 * calls, which is the ENG-9284 defect wearing a different hat.
 *
 * Linux first because the fleet is Linux (`/home/linuxbrew/.linuxbrew`); the
 * macOS prefix (`/opt/homebrew`) is for dev hosts. Both are listed rather than
 * detected because a dir that does not exist is skipped by every consumer, so
 * carrying the inapplicable one costs nothing and removes a platform branch.
 */
export const PROBE_TOOLKIT_PATH_DIRS = [
  '/home/linuxbrew/.linuxbrew/bin',
  '/opt/homebrew/bin',
] as const;

/**
 * Home-relative toolkit dirs, as path SEGMENTS (no leading slash, no `~`).
 *
 * Kept separate from {@link PROBE_TOOLKIT_PATH_DIRS} because the two consumers
 * resolve `$HOME` differently and neither can use the other's answer: the shell
 * lane expands `"${HOME:-/root}"` at run time on the target host, while the
 * Node lane has a real `homedir()`. A single pre-joined string would have to
 * pick one host's home directory at build time and be wrong on the other.
 */
export const PROBE_HOME_RELATIVE_PATH_SEGMENTS = ['.local/bin'] as const;

/**
 * The system bin dirs a probe child must always be able to see (ENG-9005).
 *
 * Copied deliberately from what `host-bootstrap.ts` writes into
 * `/etc/environment` on these hosts, so this is the host's own PATH rather than
 * a plausible-looking guess. A FLOOR: appended when absent, never a replacement,
 * and never ahead of the toolkit dirs above.
 */
export const PROBE_SYSTEM_PATH_FLOOR = [
  '/usr/local/sbin',
  '/usr/local/bin',
  '/usr/sbin',
  '/usr/bin',
  '/sbin',
  '/bin',
] as const;
